import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import {
  deleteField,
  doc,
  Firestore,
  serverTimestamp,
  updateDoc,
} from '@angular/fire/firestore';
import * as EXIF from 'exif-js';

@Component({
  selector: 'ngx-upload-profile-popup',
  templateUrl: './upload-profile-popup.component.html',
  styleUrls: ['./upload-profile-popup.component.scss']
})
export class UploadProfilePopupComponent implements OnInit {

  @ViewChild('video') video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;

  preview: any = null;
  uid: string = '';

  constructor(
    private dialogRef: NbDialogRef<UploadProfilePopupComponent>,
    private firestore: Firestore
  ) {}

  ngOnInit() {
    this.startCamera();
  }

  /** ==========================================
   *     FIX ORIENTATION FOR GALLERY IMAGES
   =========================================== */
fixOrientation(base64: string, callback: (output: string) => void) {
  const img = new Image();

  img.onload = () => {
    let MAX_WIDTH = 700;
    let MAX_HEIGHT = 700;

    let w = img.width;
    let h = img.height;

    // Resize if required
    if (w > h && w > MAX_WIDTH) {
      h = (h * MAX_WIDTH) / w;
      w = MAX_WIDTH;
    } else if (h > MAX_HEIGHT) {
      w = (w * MAX_HEIGHT) / h;
      h = MAX_HEIGHT;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    const isPortrait = img.height > img.width;

    if (isPortrait) {
      canvas.width = h;
      canvas.height = w;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(90 * Math.PI / 180);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
    } else {
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);
    }

    // 📌 Step 2: Smart Compression Loop - Always < 100 KB
    let quality = 0.9;
    let output = canvas.toDataURL("image/jpeg", quality);

    while (output.length / 1024 > 100 && quality > 0.40) {
      quality -= 0.05;
      output = canvas.toDataURL("image/jpeg", quality);
    }

    callback(output);
  };

  img.src = base64;
}




  /** ==========================================
   *                START CAMERA
   =========================================== */
  async startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }   // selfie camera
      });

      this.video.nativeElement.srcObject = stream;

    } catch (err) {
      console.log("Camera Error:", err);
    }
  }

  /** ==========================================
   *          CAMERA → MANUAL ROTATION
   =========================================== */
captureImage() {
  const video = this.video.nativeElement;
  const canvas = this.canvas.nativeElement;
  const ctx = canvas.getContext("2d")!;

  const w = video.videoWidth;
  const h = video.videoHeight;

  const isPortrait = h > w;
  const mirror = true;

  if (isPortrait) {
    canvas.width = h;
    canvas.height = w;

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(90 * Math.PI / 180);
    if (mirror) ctx.scale(-1, 1);
    ctx.drawImage(video, -w / 2, -h / 2);
    ctx.restore();

  } else {
    canvas.width = w;
    canvas.height = h;

    ctx.save();
    if (mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    ctx.restore();
  }

  // 📌 Smart compression < 100KB
  let quality = 0.9;
  let output = canvas.toDataURL("image/jpeg", quality);

  while (output.length / 1024 > 100 && quality > 0.40) {
    quality -= 0.05;
    output = canvas.toDataURL("image/jpeg", quality);
  }

  this.preview = output;

  // Stop camera
  const stream = video.srcObject as MediaStream;
  if (stream) stream.getTracks().forEach(t => t.stop());
}



  /** ==========================================
   *           GALLERY PICK (EXIF APPLY)
   =========================================== */
onGalleryPick(event: any) {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => {
    const base64 = reader.result as string;

    this.fixOrientation(base64, (corrected) => {
      this.preview = corrected;
    });
  };
  reader.readAsDataURL(file);
}


  /** ==========================================
   *              RECAPTURE BUTTON
   =========================================== */
  recapture() {
    this.preview = null;
    this.startCamera();
  }

  /** ==========================================
   *                 UPLOAD
   =========================================== */
  async upload() {
    await updateDoc(doc(this.firestore, 'users', this.uid), {
      profileImage: this.preview,
      profileUpdatedAt: serverTimestamp(),
      faceDescriptor: deleteField(),
      descriptor: deleteField(),
      faceDescriptors: deleteField(),
      faceDescriptorImageFingerprint: deleteField(),
      faceDescriptorUpdatedAt: deleteField(),
    });

    localStorage.removeItem(`portus_face_descriptor_${this.uid}`);

    try {
      const localUser = JSON.parse(localStorage.getItem('userData') || '{}');
      delete localUser.faceDescriptor;
      delete localUser.descriptor;
      delete localUser.faceDescriptors;
      delete localUser.faceDescriptorImageFingerprint;
      localStorage.setItem(
        'userData',
        JSON.stringify({ ...localUser, profileImage: this.preview }),
      );
    } catch {
      localStorage.removeItem('userData');
    }

    this.dialogRef.close(true);
  }
}
